import React from 'react';
import BrandLogo from './BrandLogo';
import './WebsiteGenerationPage.css';

const wgpImages = {
  hero: 'https://raw.githubusercontent.com/hansrajparmarcom/sajagsadhana-photos/main/photos-BeyonDigital/website-generation-page/Hero%20Image.png',
  headless: 'https://raw.githubusercontent.com/hansrajparmarcom/sajagsadhana-photos/main/photos-BeyonDigital/website-generation-page/Headless%20.png',
  portfolio: 'https://raw.githubusercontent.com/hansrajparmarcom/sajagsadhana-photos/main/photos-BeyonDigital/website-generation-page/Portfolio%20Grid.png',
  uiux: 'https://raw.githubusercontent.com/hansrajparmarcom/sajagsadhana-photos/main/photos-BeyonDigital/website-generation-page/UI_UX.png',
};

const marqueeItems = [
  'Headless CMS',
  'Next.js Performance',
  'Webflow Experts',
  'Micro-Interactions',
  'Accessibility',
  'Custom API Hooks',
];

const blueprintCards = [
  {
    id: '01',
    title: 'Purpose-Driven UX/UI',
    description:
      'Build intuitive user journeys that guide attention, reduce friction, and drive clear conversion actions.',
  },
  {
    id: '02',
    title: 'Performance Engineering',
    description:
      'Engineer fast, resilient pages with clean architecture and responsive behavior across every device.',
  },
  {
    id: '03',
    title: 'Scalable CMS Architecture',
    description:
      'Create flexible content systems that empower marketing teams while preserving development quality.',
  },
  {
    id: '04',
    title: 'Immersive Interactions',
    description:
      'Use motion, transitions, and micro-interactions to make digital experiences memorable and intuitive.',
  },
];

const gridStyles = [
  {
    title: 'The Portfolio Grid',
    description:
      'Clean, visual storytelling layouts for premium creators, studios, and modern personal brands.',
    tag: 'GRID . AESTHETIC . IDENTITY',
    image: wgpImages.portfolio,
  },
  {
    title: 'The Commerce Matrix',
    description:
      'Built for high-volume product catalogs with intuitive category flow and checkout-focused architecture.',
    tag: 'PERFORMANCE . SCALE . PRODUCT FOCUS',
    image: wgpImages.uiux,
  },
  {
    title: 'The Storyteller',
    description:
      'Narrative-first layouts with editorial pacing, cinematic rhythm, and conversion-ready structure.',
    tag: 'EDITORIAL . BRAND VOICE . IMMERSION',
    image: wgpImages.headless,
  },
];

const philosophyCards = [
  {
    quote:
      "Your website is your 24/7 salesperson. If it's slow, confusing, or outdated, you aren't just losing clicks, you're losing credibility. We treat every pixel as a trust-building asset.",
    title: 'On The First Impression',
  },
  {
    quote:
      "A beautiful site that doesn't convert is not an art. A fast site that looks bad is a tool. We believe in the third option, a high-performance machine that looks like a masterpiece. The grid provides the structure and the code provides the power.",
    title: 'On Form and Function',
  },
  {
    quote:
      "We don't build for today's browser, we build for tomorrow's internet. Our CXUI-code methodology ensures your site is accessible, SEO-optimized, and ready to integrate with the AI-driven web of 2026.",
    title: 'On Future-Proofing',
  },
];

export default function WebsiteGenerationPage() {
  return (
    <div className="wgp-page">
      <nav className="wgp-nav">
        <BrandLogo className="wgp-nav__logo" size="nav" />
        <ul className="wgp-nav__links">
          <li>
            <a href="#work">Grid Styles</a>
          </li>
          <li>
            <a href="#services">Digital Blueprint</a>
          </li>
          <li>
            <a href="#about">Our Story</a>
          </li>
        </ul>
        <a href="#contact" className="wgp-nav__cta">
          Contact
        </a>
      </nav>

      <section className="wgp-hero">
        <div className="wgp-hero__visual">
          <img
            src={wgpImages.hero}
            alt="Website generation hero"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
        <div className="wgp-hero__content">
          <p className="wgp-hero__eyebrow">Est. 2026 - London</p>
          <h1>
            Architects
            <br />
            <em>Of the</em>
            <br />
            Web
          </h1>
          <p>
            BeyonDigital.co.uk gets more than just high-performance websites done. We combine
            strategic structure with flawless engineering so your brand's first impression is
            your strongest one.
          </p>
          <div className="wgp-hero__actions">
            <a href="#work" className="wgp-btn wgp-btn--dark">
              View Grid Styles
            </a>
            <a href="#about" className="wgp-btn wgp-btn--ghost">
              Elevate Your Website ↗
            </a>
          </div>
          <div className="wgp-proof">
            <div>
              <strong>1.2&gt; Secs</strong>
              <span>Load Time</span>
            </div>
            <div>
              <strong>100%</strong>
              <span>Adaptive Fidelity</span>
            </div>
            <div>
              <strong>NaN SEO</strong>
              <span>Native Architecture</span>
            </div>
          </div>
        </div>
      </section>

      <section className="wgp-marquee">
        <div className="wgp-marquee__track">
          {[...marqueeItems, ...marqueeItems].map((item, index) => (
            <React.Fragment key={`${item}-${index}`}>
              <span>{item}</span>
              <span className="wgp-marquee__dot">·</span>
            </React.Fragment>
          ))}
        </div>
      </section>

      <section className="wgp-section" id="services">
        <header className="wgp-section__header">
          <h2>
            The <em>Digital Blueprint</em>
          </h2>
        </header>
        <div className="wgp-grid wgp-grid--services">
          {blueprintCards.map((card) => (
            <article className="wgp-service-card" key={card.title}>
              <p className="wgp-service-card__num">{card.id}</p>
              <h3>{card.title}</h3>
              <p>{card.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="wgp-section" id="work">
        <header className="wgp-section__header wgp-section__header--showcase">
          <h2>
            The Grid
            <br />
            <em>Styles</em>
          </h2>
        </header>
        <div className="wgp-grid wgp-grid--showcase">
          {gridStyles.map((item) => (
            <article className="wgp-showcase-card" key={item.title}>
              <div className="wgp-showcase-card__image">
                <img
                  src={item.image}
                  alt={item.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <div className="wgp-showcase-card__body">
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <span>{item.tag}</span>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="wgp-section" id="about">
        <header className="wgp-section__header wgp-section__header--clients">
          <h2>
            Web Generation
            <br />
            <em>Philosophy</em>
          </h2>
        </header>
        <div className="wgp-grid wgp-grid--clients">
          {philosophyCards.map((item) => (
            <blockquote className="wgp-client-card" key={item.title}>
              <span className="wgp-client-card__mark">"</span>
              <p>{item.quote}</p>
              <cite>{item.title}</cite>
            </blockquote>
          ))}
        </div>
      </section>

      <section className="wgp-cta" id="contact">
        <div className="wgp-cta__inner">
          <h2>
            Ready to own your
            <br />
            <em>corner of the web?</em>
          </h2>
          <a href="mailto:sdevarsh54@beyondigital.co.uk" className="wgp-btn wgp-btn--dark">
            Start your build phase
          </a>
        </div>
      </section>

      <footer className="wgp-footer">
        <div className="wgp-footer__top">
          <div className="wgp-footer__brand">
            <BrandLogo size="footer" asLink={false} variant="dark" />
            <p>
              Stop settling for templates. Let's build a custom digital experience that truly
              represents your brand.
            </p>
          </div>
          <div className="wgp-footer__links">
            <div>
              <h4>Agency</h4>
              <a href="/">About</a>
              <a href="/">Work</a>
              <a href="/">Careers</a>
            </div>
            <div>
              <h4>Services</h4>
              <a href="/">Strategy</a>
              <a href="/">Content</a>
              <a href="/">Paid Social</a>
            </div>
            <div>
              <h4>Connect</h4>
              <a href="https://www.instagram.com/beyondigital.co/">Instagram</a>
              <a href="/">LinkedIn</a>
              <a href="/">Twitter/X</a>
            </div>
          </div>
        </div>
        <div className="wgp-footer__bottom">London - India - Dubai</div>
      </footer>
    </div>
  );
}
